import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    EmailField,
    BooleanField,
    DateField,
    FunctionField,
    EditButton,
    Filter,
    TextInput,
    BooleanInput,
    SelectInput,
    TopToolbar,
    ExportButton,
    RefreshButton,
    useTranslate,
    useListContext,
    RecordContextProvider,
} from 'react-admin';
import { Chip, Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
    CheckCircle,
    Cancel,
    DirectionsCar,
    AccountBalanceWallet,
    Phone,
    VerifiedUser
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const AppUserFilter = (props: any) => {
    const translate = useTranslate();

    return (
        <Filter {...props}>
            <TextInput
                label={translate('resources.app-users.list.filters.search')}
                source="q"
                alwaysOn
            />
            <BooleanInput
                label={translate('resources.app-users.list.filters.activeOnly')}
                source="isActive"
            />
            <BooleanInput
                label={translate('resources.app-users.list.filters.verifiedOnly')}
                source="isVerified"
            />
            <SelectInput
                source="language"
                label={translate('resources.app-users.fields.language')}
                choices={[
                    { id: 'en', name: translate('language.en') },
                    { id: 'zh-TW', name: translate('language.zh-TW') },
                ]}
            />
        </Filter>
    );
};

const DesktopListActions = () => (
    <TopToolbar>
        <ExportButton />
        <RefreshButton />
    </TopToolbar>
);

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0 }}>
        <ExportButton />
        <RefreshButton />
    </Box>
);

const MobileList = () => {
    const { data, isLoading } = useListContext();
    const navigate = useNavigate();
    const translate = useTranslate();
    if (isLoading || !data) return null;
    return (
        <Box>
            {data.map((record: any) => (
                <RecordContextProvider key={record.id} value={record}>
                    <Box onClick={() => navigate(`/app-users/${record.id}`)}
                        sx={{ py: 1.5, px: 2, borderBottom: '1px solid #F0F0F0', cursor: 'pointer' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                                {record.name || `${record.firstName || ''} ${record.lastName || ''}`.trim() || record.phone}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                {record.isVerified && (
                                    <Chip icon={<VerifiedUser />} label={translate('resources.app-users.status.verified')} size="small" color="success" variant="outlined" sx={{ height: 22, fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: '0.85rem' } }} />
                                )}
                                <Chip
                                    label={record.isActive ? translate('resources.app-users.status.active') : translate('resources.app-users.status.inactive')}
                                    color={record.isActive ? 'success' : 'default'}
                                    size="small"
                                    sx={{ height: 22, fontSize: '0.65rem' }}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Phone sx={{ fontSize: 14, color: '#999' }} />
                                <Typography variant="caption" color="text.secondary">{record.phone}</Typography>
                            </Box>
                            {record.walletBalance !== undefined && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <AccountBalanceWallet sx={{ fontSize: 14, color: '#999' }} />
                                    <Typography variant="caption" color="text.secondary">
                                        {record.walletCurrency || 'HKD'} {(record.walletBalance || 0).toFixed(2)}
                                    </Typography>
                                    {Number(record.walletHolds) > 0 && (
                                        <Typography variant="caption" sx={{ color: '#E65100', ml: 0.5 }}>
                                            ({translate('resources.app-users.fields.hold', { _: 'Hold' })}: {record.walletCurrency || 'HKD'} {Number(record.walletHolds).toFixed(2)})
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            <Chip label={translate(`language.${record.language}`)} size="small" sx={{ height: 20, fontSize: '0.6rem', backgroundColor: '#E3F2FD', color: '#1976D2' }} />
                        </Box>
                    </Box>
                </RecordContextProvider>
            ))}
        </Box>
    );
};

export const AppUserList = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <List
            filters={<AppUserFilter />}
            actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
            perPage={25}
            sort={{ field: 'createdAt', order: 'DESC' }}
            empty={isMobile ? false : undefined}
            sx={{
                '& .RaList-content': {
                    backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
                },
                ...(isMobile && {
                    '& .RaList-main': { margin: 0 },
                    '& .RaList-content': { backgroundColor: 'transparent !important', boxShadow: 'none !important', mt: 2 },
                    '& .RaList-actions': { backgroundColor: 'transparent !important', p: '0 !important', minHeight: 'auto !important' },
                    '& .RaListToolbar-root': { backgroundColor: 'transparent !important', flexDirection: 'row !important', flexWrap: 'wrap !important', alignItems: 'center !important', gap: '8px !important' },
                    '& .RaListToolbar-root > span': { display: 'none' },
                    '& .RaFilterButton-root, & .add-filter': { display: 'none !important' },
                    '& .RaListToolbar-root > form, & .RaFilterForm-root': { flex: '1 1 100% !important', width: '100% !important' },
                    '& .filter-field, & .RaFilterForm-filterFormInput': { width: '100% !important' },
                    '& .filter-field .MuiFormControl-root': { width: '100% !important' },
                    '& .filter-field .MuiInputBase-root': { width: '100% !important' },
                    '& .MuiToolbar-root': { backgroundColor: 'transparent !important', minHeight: 'auto !important' },
                }),
            }}
        >
            {isMobile ? (
                <MobileList />
            ) : (
                <Datagrid
                    bulkActionButtons={false}
                    sx={{
                        '& .RaDatagrid-headerCell': {
                            backgroundColor: '#F5F5F5',
                            fontWeight: 600,
                        },
                        '& .RaDatagrid-row': {
                            '&:hover': {
                                backgroundColor: '#F0F7FF',
                            },
                        },
                    }}
                >
                    <FunctionField
                        label={translate('resources.app-users.fields.name')}
                        render={(record: any) => {
                            const name = record.name ||
                                `${record.firstName || ''} ${record.lastName || ''}`.trim() ||
                                record.phone;
                            return (
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {name}
                                    </Typography>
                                    {record.email && (
                                        <Typography variant="caption" color="text.secondary">
                                            {record.email}
                                        </Typography>
                                    )}
                                </Box>
                            );
                        }}
                    />

                    <FunctionField
                        label={translate('resources.app-users.fields.phone')}
                        render={(record: any) => (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Phone sx={{ fontSize: 16, color: '#666' }} />
                                <Typography variant="body2">
                                    {record.phone}
                                </Typography>
                            </Box>
                        )}
                    />

                    <FunctionField
                        label={translate('resources.app-users.fields.vehicle')}
                        render={(record: any) => {
                            if (!record.vehicleMake && !record.vehicleModel) {
                                return (
                                    <Typography variant="body2" color="text.secondary">
                                        {translate('resources.app-users.noVehicle')}
                                    </Typography>
                                );
                            }
                            return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <DirectionsCar sx={{ fontSize: 16, color: '#666' }} />
                                    <Box>
                                        <Typography variant="body2">
                                            {record.vehicleMake} {record.vehicleModel}
                                        </Typography>
                                        {record.carPlate && (
                                            <Typography variant="caption" color="text.secondary">
                                                {record.carPlate}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            );
                        }}
                    />

                    <BooleanField
                        source="isActive"
                        valueLabelTrue={translate('resources.app-users.status.active')}
                        valueLabelFalse={translate('resources.app-users.status.inactive')}
                        TrueIcon={CheckCircle}
                        FalseIcon={Cancel}
                        sx={{
                            '& .RaBooleanField-true': {
                                color: '#00C853',
                            },
                            '& .RaBooleanField-false': {
                                color: '#F44336',
                            },
                        }}
                    />

                    <FunctionField
                        label={translate('resources.app-users.fields.verified')}
                        render={(record: any) => (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {record.isVerified ? (
                                    <>
                                        <VerifiedUser sx={{ fontSize: 18, color: '#00C853' }} />
                                        <Typography variant="body2" sx={{ color: '#00C853' }}>
                                            {translate('resources.app-users.status.verified')}
                                        </Typography>
                                    </>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        {translate('resources.app-users.status.unverified')}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    />

                    <FunctionField
                        label={translate('resources.app-users.fields.wallet')}
                        render={(record: any) => {
                            // Scoped admin responses omit wallet data entirely.
                            // Check for undefined (truly absent) rather than
                            // typeof number, because Supabase may serialize
                            // `numeric` columns as strings — gating on `number`
                            // would hide wallets for super-admin too.
                            if (record.walletBalance === undefined) return null;
                            const currency = record.walletCurrency || 'HKD';
                            const holds = Number(record.walletHolds) || 0;
                            return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <AccountBalanceWallet sx={{ fontSize: 16, color: '#666' }} />
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {currency} {(record.walletBalance || 0).toFixed(2)}
                                        </Typography>
                                    </Box>
                                    {holds > 0 && (
                                        <Typography variant="caption" sx={{ color: '#E65100', pl: 2.5 }}>
                                            {translate('resources.app-users.fields.hold', { _: 'Hold' })}: {currency} {holds.toFixed(2)}
                                        </Typography>
                                    )}
                                </Box>
                            );
                        }}
                    />

                    <FunctionField
                        source="language"
                        render={(record: any) => (
                            <Chip
                                label={translate(`language.${record.language}`)}
                                size="small"
                                sx={{
                                    backgroundColor: '#E3F2FD',
                                    color: '#1976D2',
                                    fontWeight: 500,
                                }}
                            />
                        )}
                    />

                    <DateField
                        source="lastSeenAt"
                        showTime
                        options={{
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                        }}
                    />

                    <DateField
                        source="createdAt"
                        options={{
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                        }}
                    />

                    <EditButton />
                </Datagrid>
            )}
        </List>
    );
};
